import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PURPOSES, PURPOSE_LABELS, type Purpose } from '@ave/shared';
import { api, type AdminCorridor, type StatutoryAmount } from '../lib/api';
import { useSession } from '../App';
import { longDate, money } from '../lib/format';
import { Badge, Card, EmptyState, ErrorNotice, SectionHeading, Spinner } from '../components/ui';
import { AiUsePanel, ModelPricesPanel, UsagePanel } from '../components/AdminUsage';

const INPUT =
  'mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';
const SELECT =
  'mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';

const DERIVATIONS = [
  ['evidence_range', 'Evidence range — from comparable data'],
  ['fixed_band', 'Fixed band — a range somebody set'],
  ['curve_derived', 'Curve derived — computed from market data'],
] as const;

const today = () => new Date().toISOString().slice(0, 10);

const inForce = (row: { effectiveFrom: string; effectiveTo: string | null }): boolean =>
  row.effectiveFrom <= today() && (row.effectiveTo === null || row.effectiveTo > today());

/**
 * Reference data an `acumon_admin` maintains without a deploy.
 *
 * Both tables here were designed to be data rather than constants and have
 * been editable only by somebody with a psql prompt, which makes "maintained
 * without a deploy" true in the schema and false in practice.
 *
 * The screen is built around the one rule that governs both: **nothing is
 * edited in place.** A corridor widened in March did not retire the band
 * that applied in February — a valuation run then was run against the old
 * one, and the report defending it has to be able to say what the range was
 * at the time. So the form supersedes rather than updates, the superseded
 * rows stay on screen underneath, and there is no delete. That is not a
 * limitation to apologise for; it is the feature.
 *
 * Corridors and statutory amounts sit on one page and are kept visibly
 * apart, because they are different kinds of claim. A corridor is a
 * judgement about what is normal, and a figure outside it is a conversation.
 * A statutory amount is a fact fixed by Parliament, and a figure outside it
 * is not an EMI option.
 */
type AdminTab = 'reference' | 'prices' | 'usage' | 'aiUse';

const TAB_LABELS: Record<AdminTab, string> = {
  reference: 'Reference data',
  prices: 'Model prices',
  usage: 'Usage',
  aiUse: 'AI use',
};

/**
 * The blurb changes with the tab because the four are different claims, and a
 * single heading that covered all of them would have to be vague enough to
 * describe none.
 */
const BLURB: Record<AdminTab, string> = {
  reference:
    'Effective-dated, and never edited in place. A change closes what is in force and opens a new version from the date it applies — because a valuation applies the corpus as it stood at its own valuation date, and a report defending one has to be able to say what the figures were then.',
  prices:
    'What a model call costs, per million tokens. On the same footing as the corridors and for the same reason: a call is costed at the rate in force on the day it was made, so a cost already reported to licensing is never restated by a later change.',
  usage:
    'What has actually been spent, by engagement and by person, and what has been told to licensing. Pushed on a schedule; the button sends whatever is waiting now.',
  aiUse:
    'Where a language model can be invoked in this product. This is a statement of capability, not a record of use — the question a client or an insurer asks before the first engagement, which no amount of call history answers.',
};

export function AdminPage() {
  const me = useSession();
  const [tab, setTab] = useState<AdminTab>('reference');
  const [corridors, setCorridors] = useState<AdminCorridor[] | null>(null);
  const [amounts, setAmounts] = useState<StatutoryAmount[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = me.user.role === 'acumon_admin';

  async function load() {
    try {
      const [c, a] = await Promise.all([api.adminCorridors(), api.statutoryAmounts()]);
      setCorridors(c.corridors);
      setAmounts(a.amounts);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the reference data');
    }
  }

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-10">
        <ErrorNotice
          title="Reference data"
          message="Corridors and statutory amounts are platform-wide: they have no tenant, so editing them changes every firm on the system. That is an acumon_admin action."
        />
        <Link to="/" className="mt-4 inline-block text-sm text-sky-700 underline">
          Back to engagements
        </Link>
      </div>
    );
  }

  if (error) return <ErrorNotice title="Reference data" message={error} />;

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-6 py-8">
      <div>
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">
          ← Engagements
        </Link>
        <h1 className="mt-2 text-xl font-medium text-slate-900">Product admin</h1>
        <p className="mt-1 text-sm leading-relaxed text-slate-500">{BLURB[tab]}</p>
      </div>

      <nav className="flex gap-1 border-b border-slate-200">
        {(Object.keys(BLURB) as AdminTab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              tab === t
                ? 'border-teal-600 text-teal-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </nav>

      {tab === 'reference' ? (
        corridors === null || amounts === null ? (
          <Spinner label="Loading the reference data" />
        ) : (
          <>
            <Corridors corridors={corridors} onChange={load} />
            <StatutoryAmounts amounts={amounts} onChange={load} />
          </>
        )
      ) : null}
      {tab === 'prices' ? <ModelPricesPanel /> : null}
      {tab === 'usage' ? <UsagePanel /> : null}
      {tab === 'aiUse' ? <AiUsePanel /> : null}
    </div>
  );
}

function Corridors({
  corridors,
  onChange,
}: {
  corridors: AdminCorridor[];
  onChange: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState<AdminCorridor | null>(null);

  return (
    <Card>
      <SectionHeading
        title="Corridors"
        subtitle="The range a keyed assumption is expected to fall in — outside it is a stage gate and a reviewer, not a refusal"
      />

      {corridors.length === 0 ? (
        <EmptyState title="No corridors" detail="Nothing is being range-checked." />
      ) : (
        <div className="overflow-x-auto px-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="py-2 pr-3 font-medium">Assumption</th>
                <th className="py-2 pr-3 font-medium">Band</th>
                <th className="py-2 pr-3 font-medium">Applies to</th>
                <th className="py-2 pr-3 font-medium">In force</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {corridors.map((c) => (
                <tr key={c.id} className={inForce(c) ? '' : 'text-slate-400'}>
                  <td className="py-2 pr-3">
                    <span className={inForce(c) ? 'text-slate-800' : ''}>{c.assumptionKey}</span>
                    <span className="ml-2 text-xs text-slate-400">{c.module}</span>
                  </td>
                  <td className="py-2 pr-3 tabular-nums">
                    {c.params.min === undefined ? '—' : String(c.params.min)} to{' '}
                    {c.params.max === undefined ? '—' : String(c.params.max)}
                    <span className="ml-2 text-xs text-slate-400">
                      {String(c.params.unit ?? '')}
                    </span>
                  </td>
                  <td className="py-2 pr-3">
                    {c.purpose === null ? 'every purpose' : PURPOSE_LABELS[c.purpose as Purpose]}
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {longDate(c.effectiveFrom)}
                    {c.effectiveTo ? ` to ${longDate(c.effectiveTo)}` : ''}
                    {inForce(c) ? (
                      <Badge tone="emerald">current</Badge>
                    ) : (
                      <Badge tone="slate">superseded</Badge>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {inForce(c) ? (
                      <button
                        type="button"
                        className="text-xs font-medium text-slate-400 hover:text-slate-700"
                        onClick={() => {
                          setPrefill(c);
                          setOpen(true);
                        }}
                      >
                        Revise
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!open ? (
        <div className="border-t border-slate-100 px-6 py-4">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              setPrefill(null);
              setOpen(true);
            }}
          >
            Add a corridor
          </button>
        </div>
      ) : (
        <CorridorForm
          prefill={prefill}
          onClose={() => setOpen(false)}
          onSaved={async () => {
            setOpen(false);
            await onChange();
          }}
        />
      )}
    </Card>
  );
}

function CorridorForm({
  prefill,
  onClose,
  onSaved,
}: {
  prefill: AdminCorridor | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [assumptionKey, setAssumptionKey] = useState(prefill?.assumptionKey ?? '');
  const [module, setModule] = useState(prefill?.module ?? '');
  const [purpose, setPurpose] = useState<string>(prefill?.purpose ?? '');
  const [derivation, setDerivation] = useState(prefill?.derivation ?? 'evidence_range');
  const [min, setMin] = useState(prefill ? String(prefill.params.min ?? '') : '');
  const [max, setMax] = useState(prefill ? String(prefill.params.max ?? '') : '');
  const [unit, setUnit] = useState(prefill ? String(prefill.params.unit ?? '') : '');
  const [effectiveFrom, setEffectiveFrom] = useState(today());
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid =
    assumptionKey.trim() !== '' &&
    module.trim() !== '' &&
    (min.trim() !== '' || max.trim() !== '') &&
    reason.trim().length >= 10;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.setCorridor({
        assumptionKey: assumptionKey.trim(),
        module: module.trim(),
        purpose: purpose === '' ? null : purpose,
        derivation,
        params: {
          ...(min.trim() === '' ? {} : { min: min.trim() }),
          ...(max.trim() === '' ? {} : { max: max.trim() }),
          ...(unit.trim() === '' ? {} : { unit: unit.trim() }),
        },
        effectiveFrom,
        reason: reason.trim(),
      });
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the corridor');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border-t border-slate-100 px-6 py-5">
      {prefill ? (
        <p className="mb-3 text-sm leading-relaxed text-slate-500">
          Revising <span className="text-slate-800">{prefill.assumptionKey}</span>. The band in
          force is closed the day this one starts and stays on the list, because valuations run
          before then were run against it.
        </p>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-slate-600">
          Assumption key
          <input
            className={INPUT}
            value={assumptionKey}
            placeholder="discount.dlom"
            disabled={prefill !== null}
            onChange={(e) => setAssumptionKey(e.target.value)}
          />
        </label>

        <label className="text-sm text-slate-600">
          Module
          <input
            className={INPUT}
            value={module}
            placeholder="discounts"
            onChange={(e) => setModule(e.target.value)}
          />
        </label>

        <label className="text-sm text-slate-600">
          Applies to
          <select className={SELECT} value={purpose} onChange={(e) => setPurpose(e.target.value)}>
            <option value="">Every purpose</option>
            {PURPOSES.map((p) => (
              <option key={p} value={p}>
                {PURPOSE_LABELS[p]}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm text-slate-600">
          Where the band comes from
          <select
            className={SELECT}
            value={derivation}
            onChange={(e) => setDerivation(e.target.value as typeof derivation)}
          >
            {DERIVATIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <label className="text-sm text-slate-600">
          Minimum
          <input
            className={INPUT}
            inputMode="decimal"
            value={min}
            placeholder="Leave blank for no floor"
            onChange={(e) => setMin(e.target.value)}
          />
        </label>
        <label className="text-sm text-slate-600">
          Maximum
          <input
            className={INPUT}
            inputMode="decimal"
            value={max}
            placeholder="Leave blank for no ceiling"
            onChange={(e) => setMax(e.target.value)}
          />
        </label>
        <label className="text-sm text-slate-600">
          Unit
          <input
            className={INPUT}
            value={unit}
            placeholder="ratio, pct, x"
            onChange={(e) => setUnit(e.target.value)}
          />
        </label>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-slate-600">
          Applies from
          <input
            className={INPUT}
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
          />
        </label>
        <label className="text-sm text-slate-600">
          Why it is moving
          <input
            className={INPUT}
            value={reason}
            placeholder="The evidence set was refreshed to March 2026"
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
      </div>

      <p className="mt-3 text-sm leading-relaxed text-slate-500">
        A figure outside the band is not refused. It stops the stage gate until a reviewer approves
        the exception with a justification, which is the conversation the corridor exists to
        provoke.
      </p>

      {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}

      <div className="mt-4 flex gap-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={submit} disabled={!valid || busy}>
          {prefill ? 'Supersede it' : 'Add it'}
        </button>
      </div>
    </div>
  );
}

function StatutoryAmounts({
  amounts,
  onChange,
}: {
  amounts: StatutoryAmount[];
  onChange: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Card>
      <SectionHeading
        title="Statutory amounts"
        subtitle="Fixed by Parliament, applied as at the valuation date — the EMI individual limit was £120,000 until June 2012"
      />

      {amounts.length === 0 ? (
        <EmptyState title="No statutory amounts" detail="Nothing is being limit-checked." />
      ) : (
        <div className="overflow-x-auto px-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="py-2 pr-3 font-medium">Limit</th>
                <th className="py-2 pr-3 text-right font-medium">Amount</th>
                <th className="py-2 pr-3 font-medium">In force</th>
                <th className="py-2 font-medium">Authority</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {amounts.map((a) => (
                <tr key={a.id} className={inForce(a) ? '' : 'text-slate-400'}>
                  <td className="py-2 pr-3">
                    <span className={inForce(a) ? 'text-slate-800' : ''}>{a.label}</span>
                    <span className="ml-2 text-xs text-slate-400">{a.amountKey}</span>
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{money(a.amount)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {longDate(a.effectiveFrom)}
                    {a.effectiveTo ? ` to ${longDate(a.effectiveTo)}` : ''}
                  </td>
                  {/* The citation is the point of the row. A statutory limit
                      with nothing behind it is a number somebody remembered,
                      and it will be read back later as a fact. */}
                  <td className="py-2 text-xs text-slate-500">{a.citation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!open ? (
        <div className="border-t border-slate-100 px-6 py-4">
          <button type="button" className="btn-secondary" onClick={() => setOpen(true)}>
            Record an amount
          </button>
        </div>
      ) : (
        <AmountForm
          existingKeys={[...new Set(amounts.map((a) => a.amountKey))]}
          onClose={() => setOpen(false)}
          onSaved={async () => {
            setOpen(false);
            await onChange();
          }}
        />
      )}
    </Card>
  );
}

function AmountForm({
  existingKeys,
  onClose,
  onSaved,
}: {
  existingKeys: string[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [amountKey, setAmountKey] = useState(existingKeys[0] ?? '');
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [citation, setCitation] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(today());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid =
    amountKey.trim() !== '' &&
    label.trim() !== '' &&
    /^\d+(\.\d{1,2})?$/.test(amount.trim()) &&
    citation.trim() !== '';

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.setStatutoryAmount({
        amountKey: amountKey.trim(),
        label: label.trim(),
        amount: amount.trim(),
        citation: citation.trim(),
        effectiveFrom,
      });
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the amount');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border-t border-slate-100 px-6 py-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-slate-600">
          Key
          <input
            className={INPUT}
            value={amountKey}
            placeholder="emi.individual_limit"
            list="statutory-keys"
            onChange={(e) => setAmountKey(e.target.value)}
          />
          <datalist id="statutory-keys">
            {existingKeys.map((k) => (
              <option key={k} value={k} />
            ))}
          </datalist>
        </label>

        <label className="text-sm text-slate-600">
          What it is called
          <input
            className={INPUT}
            value={label}
            placeholder="EMI individual limit"
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>

        <label className="text-sm text-slate-600">
          Amount
          <input
            className={INPUT}
            inputMode="decimal"
            value={amount}
            placeholder="250000"
            onChange={(e) => setAmount(e.target.value)}
          />
        </label>

        <label className="text-sm text-slate-600">
          In force from
          <input
            className={INPUT}
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
          />
        </label>
      </div>

      <label className="mt-3 block text-sm text-slate-600">
        The provision that fixes it
        <input
          className={INPUT}
          value={citation}
          placeholder="ITEPA 2003 Sch 5 para 5(1), as amended by FA 2012 s.27"
          onChange={(e) => setCitation(e.target.value)}
        />
      </label>

      <p className="mt-3 text-sm leading-relaxed text-slate-500">
        The figure in force at a valuation's own date is the one it uses, so this does not change
        any valuation already run. The previous amount is closed the day this one starts and stays
        on the list.
      </p>

      {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}

      <div className="mt-4 flex gap-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={submit} disabled={!valid || busy}>
          Record it
        </button>
      </div>
    </div>
  );
}
